import {
  RefreshError,
  refreshAccessToken,
  serverRemoveAccessToken,
} from "@/api";
import { reportAuthEvent } from "@/lib/authDiagnostics";
import {
  featuresService,
  haveFeaturesChanged,
} from "@/services/featuresService";
import { EventStreamContentType } from "@microsoft/fetch-event-source";
import { authSession } from "./authSession";
import type { createExponentialBackoff } from "./exponentialBackoff";
import { WEB_CLIENT_HEADER, WEB_CLIENT_VALUE } from "./webClient";

// Autenticação dos streams SSE (fetchEventSource) integrada ao refresh token.
//
// Os streams não passam pelo axios, então o interceptor de 401 → refresh do
// `@/api` nunca entra. Antes, cada reconexão relia o access_token do storage
// e, se ele já tivesse expirado (~1h) sem nenhuma chamada REST na aba para
// renová-lo, o stream ficava em loop 401 → backoff → 401 até alguma request
// REST disparar o refresh. Estes helpers fecham esse buraco:
//
//  - `authenticatedSSEFetch`: renova o token ANTES de (re)conectar quando ele
//    já expirou (barato: `refreshAccessToken` devolve o token atual sem request
//    quando ainda vale; single-flight por aba e lock entre abas).
//  - `sseOnOpen`: se o servidor mesmo assim responder 401 (token revogado,
//    relógio adiantado…), força a renovação a partir do token stale e lança
//    para o fetchEventSource reconectar já com o token novo.
//  - `createSSEErrorHandler`: backoff exponencial normal, exceto quando o
//    refresh token é definitivamente inválido — aí encerra a sessão e para de
//    reconectar.

const readStoredToken = () => localStorage.getItem("access_token") ?? "";

// Mesmo contrato do interceptor axios: se o refresh trouxe um conjunto de
// features diferente (permissões mudaram durante a sessão), recarrega a
// página para reaplicar o gating da UI — useFeature/FeatureGuard leem as
// features uma única vez, e o caminho SSE não passa pelo interceptor, então
// sem isso a mudança ficaria invisível até um reload manual. O guard de
// `before` não-vazio evita reload em loop no boot (features ainda ausentes).
const refreshTrackingFeatures = async (
  staleToken?: string
): Promise<string> => {
  const before = featuresService.getFeatures();
  const token = await refreshAccessToken(staleToken);

  if (
    before.length > 0 &&
    haveFeaturesChanged(before, featuresService.getFeatures())
  ) {
    window.location.reload();
  }

  return token;
};

export const authenticatedSSEFetch: typeof fetch = async (input, init) => {
  let token: string;
  try {
    token = await refreshTrackingFeatures();
  } catch {
    // Falha transitória ou tokens ausentes: tenta com o que há no storage. Se
    // o servidor rejeitar, `sseOnOpen` trata o 401 (inclusive o logout).
    token = readStoredToken();
  }

  const headers = new Headers(init?.headers);

  if (authSession.isCookieMode()) {
    headers.set(WEB_CLIENT_HEADER, WEB_CLIENT_VALUE);
    return fetch(input, { ...init, headers, credentials: "include" });
  }

  headers.set("Authorization", `Bearer ${token}`);
  return fetch(input, { ...init, headers });
};

export const sseOnOpen = async (response: Response): Promise<void> => {
  if (response.status === 401) {
    reportAuthEvent("auth_request_401", {
      path: response.url ? new URL(response.url).pathname : null,
      method: "GET",
      source: "sse",
    });

    // Passa o token que acabou de ser rejeitado: `refreshAccessToken` força a
    // renovação se ele ainda for o do storage; se outra aba já renovou,
    // apenas devolve o token novo sem gastar o refresh token rotativo.
    await refreshTrackingFeatures(authSession.credentialMarker() || undefined);
    // Lança para cair em `onerror` → reconecta com o token renovado.
    throw new Error("sse_unauthorized_retry");
  }

  // Mesma validação do `onopen` padrão da lib.
  const contentType = response.headers.get("content-type");
  if (!contentType?.startsWith(EventStreamContentType)) {
    throw new Error(
      `Expected content-type to be ${EventStreamContentType}, Actual: ${contentType}`
    );
  }
};

type Backoff = ReturnType<typeof createExponentialBackoff>;

/**
 * `onerror` padrão dos streams: retorna o delay (não lança) para o
 * fetchEventSource reconectar com backoff. Só interrompe a reconexão quando o
 * refresh token é inválido (401/403 no refresh ou tokens ausentes) — nesse
 * caso a sessão é encerrada, como no interceptor do axios.
 */
export const createSSEErrorHandler =
  (backoff: Backoff) =>
  (error: unknown): number => {
    if (error instanceof RefreshError && error.shouldLogout) {
      void serverRemoveAccessToken("refresh_rejected", {
        source: "sse",
        refresh_error: error.message,
        refresh_status: error.status ?? null,
      });
      throw error;
    }
    return backoff.next();
  };
